import * as React from 'react';
import { normalise, deriveSize, getDataProps } from '@lendi-ui/utils';
import styled, { css, ThemeContext } from 'styled-components';
import { fg, bg, color } from '@lendi-ui/color';
import { depth } from '@lendi-ui/depth';
import { map } from '@lendi-ui/breakpoint';
import { getRadius } from '@lendi-ui/commons/borders';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Spacing } from '@lendi-ui/spacing';
import { select } from '@lendi/ui/Theme';
import Spinner from '@lendi-ui/spinner';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var commonStyleBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["border-radius:21px;padding:", " ", ";font-size:", ";letter-spacing:", ";"], deriveSize(0.5), deriveSize(0.5625), deriveSize(0.625), _ref => {
        var {
          theme
        } = _ref;
        return theme.name === 'lendi' ? deriveSize(0.0625) : 'normal';
      });
    case 'sm':
      return css(["height:", ";padding:0 ", ";font-size:", ";letter-spacing:", ";"], deriveSize(2.5), deriveSize(0.75), deriveSize(0.75), _ref2 => {
        var {
          theme
        } = _ref2;
        return theme.name === 'lendi' ? deriveSize(0.05) : 'normal';
      });
    case 'md':
      return css(["height:", ";padding:0 ", ";font-size:", ";letter-spacing:", ";"], deriveSize(3), deriveSize(1), deriveSize(0.875), _ref3 => {
        var {
          theme
        } = _ref3;
        return theme.name === 'lendi' ? deriveSize(0.0625) : 'normal';
      });
    case 'lg':
      return css(["height:", ";padding:0 ", ";font-size:", ";letter-spacing:", ";"], deriveSize(4), deriveSize(1.5), deriveSize(1), _ref4 => {
        var {
          theme
        } = _ref4;
        return theme.name === 'lendi' ? deriveSize(0.075) : 'normal';
      });
    default:
      return undefined;
  }
});
var beforeBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
    case 'sm':
      return "\n          margin-right: ".concat(deriveSize(0.5), ";\n      ");
    case 'md':
    case 'lg':
      return "\n         margin-right: ".concat(deriveSize(1), ";\n      ");
    default:
      return undefined;
  }
});
var afterBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
    case 'sm':
      return "\n          margin-left: ".concat(deriveSize(0.5), ";\n      ");
    case 'md':
    case 'lg':
      return "\n         margin-left: ".concat(deriveSize(1), ";\n      ");
    default:
      return undefined;
  }
});
var commonStyle$1 = css(["", ";cursor:pointer;box-sizing:border-box;border:none;border-radius:", ";font-family:", ";font-weight:", ";line-height:1;text-align:center;transition:all 0.1s;text-transform:uppercase;", " ", " &:focus{outline:none;", ";}&:hover{", ";}&:active{transform:scale(0.98);}", ""], normalise, getRadius('button'), _ref5 => {
  var {
    theme
  } = _ref5;
  return theme.asset.font.body.family;
}, _ref6 => {
  var {
    theme
  } = _ref6;
  return theme.asset.font.heading.weight;
}, _ref7 => {
  var {
    size
  } = _ref7;
  return css(["", ";"], size && commonStyleBySizeMixin(size));
}, _ref8 => {
  var {
    isFullWidth = false
  } = _ref8;
  return isFullWidth && css(["width:100%;"]);
}, depth(2), depth(4), _ref9 => {
  var {
    isDisabled,
    theme
  } = _ref9;
  return isDisabled ? css(["", ";pointer-events:inherit;cursor:not-allowed;box-shadow:none;transform:scale(1);:hover,:active,:focus{pointer-events:inherit;cursor:not-allowed;box-shadow:none;transform:scale(1);}"], theme.name === 'lendi' && "opacity: 0.4;") : undefined;
});
var themeAble = colourPrefix => colourPrefix === 'primary' || colourPrefix === 'secondary';
var filledStyle = _ref10 => {
  var {
    colorPrefix
  } = _ref10;
  return css(["", ";border-radius:", ";", " ", " &:focus{", "}&:hover{", ";box-shadow:inset 0 0 0 1px ", ";}&:active{", "}", ""], commonStyle$1, getRadius('button'), _ref11 => {
    var {
      theme
    } = _ref11;
    return themeAble(colorPrefix) ? "color: ".concat(theme.color.buttonForeground[colorPrefix].default, ";") : fg('shade.0');
  }, _ref12 => {
    var {
      theme
    } = _ref12;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].default, ";") : bg("".concat(colorPrefix, ".500"));
  }, _ref13 => {
    var {
      theme
    } = _ref13;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].focus, ";") : bg("".concat(colorPrefix, ".400"));
  }, _ref14 => {
    var {
      theme
    } = _ref14;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].hover, ";") : bg("".concat(colorPrefix, ".400"));
  }, _ref15 => {
    var {
      theme
    } = _ref15;
    return themeAble(colorPrefix) ? theme.color.buttonBorder[colorPrefix].disabled : color("".concat(colorPrefix, ".500"));
  }, _ref16 => {
    var {
      theme
    } = _ref16;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].pressed, ";") : bg("".concat(colorPrefix, ".600"));
  }, _ref17 => {
    var {
      isDisabled
    } = _ref17;
    return isDisabled ? css(["", ";color:", ";box-shadow:inset 0 0 0 1px ", ";&:active,&:hover,&:focus{", "}"], _ref18 => {
      var {
        theme
      } = _ref18;
      return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].disabled, ";") : bg("".concat(colorPrefix, ".500"));
    }, _ref19 => {
      var {
        theme
      } = _ref19;
      return theme.color.buttonForeground.primary.disabled;
    }, _ref20 => {
      var {
        theme
      } = _ref20;
      return theme.color.buttonBorder.primary.disabled;
    }, _ref21 => {
      var {
        theme
      } = _ref21;
      return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].disabled, ";") : bg("".concat(colorPrefix, ".500"));
    }) : undefined;
  });
};
var inversedFilledStyle = _ref22 => {
  var {
    colorPrefix
  } = _ref22;
  return css(["", ";border-radius:", ";", ";", ";&:active{opacity:0.9;}"], commonStyle$1, getRadius('button'), _ref23 => {
    var {
      theme
    } = _ref23;
    return themeAble(colorPrefix) ? "color: ".concat(theme.color.buttonForeground.secondary.default, ";") : fg("".concat(colorPrefix, ".500"));
  }, _ref24 => {
    var {
      theme
    } = _ref24;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill.secondary.default, ";") : bg('shade.0');
  });
};
var outlineStyle = _ref25 => {
  var {
    colorPrefix
  } = _ref25;
  return css(["", ";border-radius:", ";", " ", ";border:1px solid ", ";&:active{opacity:0.9;}&:disabled{color:", ";border:1px solid ", ";}"], commonStyle$1, getRadius('button'), _ref26 => {
    var {
      theme
    } = _ref26;
    return themeAble(colorPrefix) ? "color: ".concat(theme.color.buttonForeground[colorPrefix].default, ";") : fg("".concat(colorPrefix, ".500"));
  }, _ref27 => {
    var {
      theme
    } = _ref27;
    return themeAble(colorPrefix) ? "background-color: ".concat(theme.color.buttonFill[colorPrefix].default, ";") : bg('shade.0');
  }, _ref28 => {
    var {
      theme
    } = _ref28;
    return themeAble(colorPrefix) ? theme.color.buttonBorder[colorPrefix].default : color("".concat(colorPrefix, ".500"));
  }, _ref29 => {
    var {
      theme
    } = _ref29;
    return theme.color.buttonForeground.secondary.disabled;
  }, _ref30 => {
    var {
      theme
    } = _ref30;
    return themeAble(colorPrefix) ? theme.color.buttonBorder[colorPrefix].disabled : color("".concat(colorPrefix, ".500"));
  });
};
var inversedOutlineStyle = () => {
  return css(["", ";border-radius:", ";", " background-color:transparent;border:1px solid ", ";&:active{opacity:0.9;}"], commonStyle$1, getRadius('button'), fg('shade.0'), color('shade.0'));
};
var buttonStyle = _ref31 => {
  var {
    variant,
    isInverse
  } = _ref31;
  switch (variant) {
    case 'primary':
      return !isInverse ? filledStyle({
        colorPrefix: 'primary'
      }) : inversedFilledStyle({
        colorPrefix: 'primary'
      });
    case 'secondary':
      return !isInverse ? outlineStyle({
        colorPrefix: 'secondary'
      }) : inversedOutlineStyle();
    case 'emphasis':
      return filledStyle({
        colorPrefix: 'tertiary'
      });
    case 'empty':
      return css(["", " border-style:dashed;&:focus{border-width:1px;}"], !isInverse ? outlineStyle({
        colorPrefix: 'secondary'
      }) : inversedOutlineStyle());
  }
};
var ButtonWrapper = styled.button.withConfig({
  displayName: "indexstyle__ButtonWrapper",
  componentId: "lui__sc-1m54lw8-0"
})(["", ";"], buttonStyle);
var LinkWrapper$1 = styled(ButtonWrapper).attrs({
  as: 'a'
}).withConfig({
  displayName: "indexstyle__LinkWrapper",
  componentId: "lui__sc-1m54lw8-1"
})(["display:inline-block;text-decoration:none;"]);
var Layout = styled.div.withConfig({
  displayName: "indexstyle__Layout",
  componentId: "lui__sc-1m54lw8-2"
})(["display:flex;height:100%;align-items:center;justify-content:space-between;"]);
var BeforeWrapper = styled.span.withConfig({
  displayName: "indexstyle__BeforeWrapper",
  componentId: "lui__sc-1m54lw8-3"
})(["", ";display:flex;"], _ref32 => {
  var {
    size
  } = _ref32;
  return beforeBySizeMixin(size);
});
var AfterWrapper = styled.span.withConfig({
  displayName: "indexstyle__AfterWrapper",
  componentId: "lui__sc-1m54lw8-4"
})(["", ";display:flex;"], _ref33 => {
  var {
    size
  } = _ref33;
  return afterBySizeMixin(size);
});

var _excluded$2 = ["href", "isDisabled", "type"];
class Button extends React.Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "handleLinkClick", event => {
      var {
        isDisabled,
        onClick
      } = this.props;
      if (isDisabled) {
        event.preventDefault();
      } else {
        if (onClick) {
          onClick(event);
        }
      }
    });
  }
  get commonProps() {
    var {
      variant,
      size = 'md',
      isInverse,
      isFullWidth,
      isDisabled,
      className,
      onClick
    } = this.props;
    return {
      variant,
      size,
      isInverse,
      isFullWidth,
      isDisabled,
      className,
      onClick
    };
  }
  get linkProps() {
    var {
      href
    } = this.props;
    return {
      href
    };
  }
  renderContent() {
    var {
      size = 'md',
      before,
      after,
      children
    } = this.props;
    return /*#__PURE__*/jsxs(Layout, {
      children: [/*#__PURE__*/jsx(BeforeWrapper, {
        size: size,
        children: before
      }), /*#__PURE__*/jsx("span", {
        children: children
      }), /*#__PURE__*/jsx(AfterWrapper, {
        size: size,
        children: after
      })]
    });
  }
  render() {
    var _this$props = this.props,
      {
        href,
        isDisabled,
        type = 'button'
      } = _this$props,
      otherProps = _objectWithoutProperties(_this$props, _excluded$2);
    if (href) {
      return /*#__PURE__*/jsx(LinkWrapper$1, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, otherProps), this.commonProps), this.linkProps), {}, {
        onClick: this.handleLinkClick,
        children: this.renderContent()
      }));
    } else {
      var {
        ariaLabel = '',
        children
      } = this.props;
      var buttonLabel = ariaLabel;

      // infer the aria-label from children if possible
      if (!ariaLabel.length && typeof children === 'string') {
        buttonLabel = children;
      }
      return /*#__PURE__*/jsx(ButtonWrapper, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
        "aria-label": buttonLabel,
        disabled: isDisabled,
        type: type
      }, getDataProps(this.props)), this.commonProps), otherProps), {}, {
        children: this.renderContent()
      }));
    }
  }
}

var commonStyle = css(["", ";cursor:pointer;border:none;border-radius:", ";text-align:center;transition:all 0.1s;background:inherit;:focus{background:none;}:hover{background:inherit;background:rgba(74,74,74,0.15);}:active{background:inherit;background:rgba(74,74,74,0.2);}", " ", ""], normalise, select('borderRadius'), _ref => {
  var {
    size
  } = _ref;
  switch (size) {
    case 'xs':
      return css(["padding:", ";"], deriveSize(0.375));
    case 'sm':
      return css(["padding:", ";"], deriveSize(0.625));
    case 'md':
      return css(["padding:", ";"], deriveSize(0.75));
    case 'lg':
      return css(["padding:", ";"], deriveSize(1));
    default:
      return undefined;
  }
}, _ref2 => {
  var {
    disabled
  } = _ref2;
  return disabled ? css(["border-color:", ";pointer-events:inherit;cursor:not-allowed;opacity:0.4;transform:scale(1);:hover,:active,:focus{background:inherit;pointer-events:inherit;cursor:not-allowed;opacity:0.4;box-shadow:none;transform:scale(1);}"], color('error.500')) : undefined;
});
var IconButtonWrapper = styled.button.withConfig({
  displayName: "indexstyle__IconButtonWrapper",
  componentId: "lui__sc-xvll15-0"
})(["", ";background:inherit;"], commonStyle);
var LinkWrapper = styled(IconButtonWrapper.withComponent('a')).withConfig({
  displayName: "indexstyle__LinkWrapper",
  componentId: "lui__sc-xvll15-1"
})(["", ";text-decoration:none;"], commonStyle);

var spacingTopBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
    case 'sm':
    case 'md':
    case 'lg':
      return css(["margin-top:calc(-1 * ", ");"], Spacing[spacingBySize[val]]);
    default:
      return undefined;
  }
});
var spacingRightBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
    case 'sm':
    case 'md':
    case 'lg':
      return css(["margin-right:calc(-1 * ", ");"], Spacing[spacingBySize[val]]);
    default:
      return undefined;
  }
});
var wrapperBySizeMixin = (size, isFullWidth) => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["margin-top:", ";", ";"], deriveSize(0.5), !isFullWidth && "margin-right: ".concat(deriveSize(0.5)));
    case 'sm':
      return css(["margin-top:", ";", ";"], deriveSize(0.75), !isFullWidth && "margin-right: ".concat(deriveSize(0.75)));
    case 'md':
      return css(["margin-top:", ";", ";"], deriveSize(1), !isFullWidth && "margin-right: ".concat(deriveSize(1)));
    case 'lg':
      return css(["margin-top:", ";", ";"], deriveSize(1.5), !isFullWidth && "margin-right: ".concat(deriveSize(1.5)));
    default:
      return undefined;
  }
});
var spacingBySize = {
  lg: 'md',
  md: 'sm',
  sm: 'xs',
  xs: 'xxs'
};
var Wrapper = styled.div.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-obdqx3-0"
})(["", ";display:inline-flex;flex-wrap:wrap;", ";", " ", ",", "{", ";}"], normalise, _ref => {
  var {
    size
  } = _ref;
  return spacingTopBySizeMixin(size);
}, _ref2 => {
  var {
    size,
    isFullWidth
  } = _ref2;
  return isFullWidth && css(["width:100%;", ";"], spacingRightBySizeMixin(size));
}, ButtonWrapper, IconButtonWrapper, _ref3 => {
  var {
    size,
    isFullWidth
  } = _ref3;
  return css(["", ";"], wrapperBySizeMixin(size, isFullWidth));
});

var _excluded$1 = ["isLoading", "href", "color", "size", "isDisabled", "icon", "onClick", "type"];
class IconButton extends React.Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "handleLinkClick", event => {
      var {
        isDisabled,
        onClick
      } = this.props;
      if (isDisabled) {
        event.preventDefault();
      } else {
        if (onClick) {
          onClick(event);
        }
      }
    });
    _defineProperty(this, "getIconSize", size => {
      switch (size) {
        case 'xs':
          return deriveSize(1);
        case 'sm':
          return deriveSize(1.25);
        case 'md':
          return deriveSize(1.5);
        case 'lg':
          return deriveSize(2);
        default:
          return undefined;
      }
    });
  }
  render() {
    var theme = this.context;
    var _this$props = this.props,
      {
        isLoading = false,
        href,
        color = theme.color.interaction.main,
        size = 'md',
        isDisabled,
        icon,
        onClick,
        type = 'button'
      } = _this$props,
      otherProps = _objectWithoutProperties(_this$props, _excluded$1);
    var Icon = isLoading ? Spinner : this.props.icon;
    var iconSize = this.getIconSize(size);
    if (href) {
      return /*#__PURE__*/jsx(LinkWrapper, _objectSpread2(_objectSpread2({
        onClick: this.handleLinkClick,
        size: size,
        disabled: isDisabled,
        href: href
      }, otherProps), {}, {
        children: /*#__PURE__*/jsx(Icon, {
          color: color,
          height: iconSize,
          width: iconSize
        })
      }));
    } else {
      return /*#__PURE__*/jsx(IconButtonWrapper, _objectSpread2(_objectSpread2(_objectSpread2({
        onClick: onClick,
        size: size,
        disabled: isDisabled || isLoading,
        type: type
      }, getDataProps(this.props)), otherProps), {}, {
        children: /*#__PURE__*/jsx(Icon, {
          color: color,
          height: iconSize,
          width: iconSize
        })
      }));
    }
  }
}
_defineProperty(IconButton, "contextType", ThemeContext);

var _excluded = ["size", "className", "isFullWidth", "children", "isInverse", "isLoading", "isDisabled"];
var ButtonGroupButton = props => /*#__PURE__*/jsx(ButtonContext.Consumer, {
  children: state => /*#__PURE__*/jsx(Button, _objectSpread2(_objectSpread2({}, props), state))
});
var ButtonGroupIconButton = props => /*#__PURE__*/jsx(ButtonContext.Consumer, {
  children: state => /*#__PURE__*/jsx(IconButton, _objectSpread2(_objectSpread2({}, props), state))
});
var ButtonContext = /*#__PURE__*/React.createContext({});
class ButtonGroup extends React.Component {
  render() {
    var _this$props = this.props,
      {
        size = 'md',
        className,
        isFullWidth,
        children,
        isInverse,
        isLoading = false,
        isDisabled
      } = _this$props,
      btnProps = _objectWithoutProperties(_this$props, _excluded);
    var buttonGroupWrapperProps = {
      size,
      isFullWidth,
      isInverse,
      isDisabled,
      isLoading
    };
    return /*#__PURE__*/jsx(ButtonContext.Provider, {
      value: buttonGroupWrapperProps,
      children: /*#__PURE__*/jsx(Wrapper, _objectSpread2(_objectSpread2({
        className: className,
        size: size,
        isFullWidth: isFullWidth
      }, btnProps), {}, {
        children: children
      }))
    });
  }
}
_defineProperty(ButtonGroup, "Button", ButtonGroupButton);
_defineProperty(ButtonGroup, "IconButton", ButtonGroupIconButton);

export { Button, ButtonGroup, IconButton };
